import { Box, Flex, Heading } from '@radix-ui/themes'
import { type TFunction } from 'i18next'
import { useKeenSlider } from 'keen-slider/react.js'
import * as React from 'react'

import { Image, type Picture } from '#app/components/ui/image'

/* -------------------------------------------------------------------------------------------------
 * Partners
 * -----------------------------------------------------------------------------------------------*/

enum TEST_ID {
	partners = 'partners',
	'partner-logo' = 'partner-logo',
}

type Partner = {
	link: string
	picture: Picture
}

const partners: Partner[] = [
	{
		link: 'https://claudiaraabe.com/',
		picture: {
			img: {
				src: '/pwa/partner/claudia_raabe_logo.webp?w=300',
				alt: 'Claudia Raabe',
				width: 200,
				height: 100,
			},
		},
	},
	{
		link: 'https://kurt-gifhorn.de/',
		picture: {
			img: {
				src: '/pwa/partner/kurt_logo.webp?w=300',
				alt: 'Kurt',
				width: 200,
				height: 100,
			},
		},
	},
	{
		link: 'https://www.leder-faber.de/',
		picture: {
			img: {
				src: '/pwa/partner/faber_logo.webp?w=300',
				alt: 'Faber',
				width: 200,
				height: 100,
			},
		},
	},
	{
		link: 'https://www.ricogerber.de/',
		picture: {
			img: {
				src: '/pwa/partner/rico_gerber_logo.webp?w=300',
				alt: 'Faber',
				width: 200,
				height: 100,
			},
		},
	},
	{
		link: 'https://www.anjaboerner.de/',
		picture: {
			img: {
				src: '/pwa/partner/anja_boerner.webp?w=300',
				alt: 'Faber',
				width: 200,
				height: 100,
			},
		},
	},
	{
		link: 'https://www.cp-dance.com/',
		picture: {
			img: {
				src: '/pwa/partner/cp_dance_logo.webp?w=300',
				alt: 'Faber',
				width: 200,
				height: 100,
			},
		},
	},
]

type PartnersProps = {
	t: TFunction<string, undefined>
}

const Partners: React.FC<PartnersProps> = ({ t }) => {
	const animation = { duration: 50000, easing: (t: number) => t }

	const [sliderRef] = useKeenSlider<HTMLDivElement>({
		loop: true,
		renderMode: 'performance',
		drag: false,
		slides: {
			perView: () => Math.min(window.innerWidth / 200, 6),
			spacing: 40,
		},
		created(s) {
			s.moveToIdx(5, true, animation)
		},
		updated(s) {
			s.moveToIdx(s.track.details.abs + 5, true, animation)
		},
		animationEnded(s) {
			s.moveToIdx(s.track.details.abs + 5, true, animation)
		},
	})

	return (
		<Flex
			direction="column"
			gapY="5"
			my="8"
			px="4"
			data-testid={TEST_ID.partners}
		>
			<Heading size="8" color="gray" align="center">
				{t('common.partners.title')}
			</Heading>
			<Box ref={sliderRef} className="keen-slider">
				{partners.map(({ link, picture }: Partner) => (
					<a
						key={link}
						href={link}
						target="_blank"
						rel="noreferrer"
						className="keen-slider__slide flex items-center justify-center"
						data-testid={TEST_ID['partner-logo']}
					>
						<Image
							api={false}
							alt={picture.img.alt}
							src={picture.img.src}
							picture={picture}
							loading="lazy"
						/>
					</a>
				))}
			</Box>
		</Flex>
	)
}
Partners.displayName = 'Partners'

/* -----------------------------------------------------------------------------------------------*/

export { Partners, type Partner, TEST_ID as PARTNER_TEST_ID }
